import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vShow as _vShow, withCtx as _withCtx, createVNode as _createVNode, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-ed9acb10"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "panel" }
const _hoisted_2 = { class: "section" }
const _hoisted_3 = { class: "section-header" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h4", null, "水位簡易グラフ", -1))
const _hoisted_5 = { class: "graph-wrapper" }
const _hoisted_6 = { class: "base" }
const _hoisted_7 = { class: "units" }
const _hoisted_8 = { class: "x-units" }
const _hoisted_9 = { class: "y-units" }
const _hoisted_10 = { class: "plot-area" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_12 = {
  viewBox: "0 0 520 265",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_13 = ["x1", "y1", "x2", "y2"]
const _hoisted_14 = ["y1", "y2", "stroke", "onMouseenter"]
const _hoisted_15 = { class: "chobos" }
const _hoisted_16 = ["onMouseenter"]
const _hoisted_17 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "dummy-area" }, null, -1))
const _hoisted_18 = [
  _hoisted_17
]

import { computed, ref } from 'vue'
import $ from 'jquery'
import moment from 'moment'
import { reverse, times, camelCase } from 'lodash'
import Tooltip from '@/components/common/Tooltip.vue'
import { HistoryItem } from '@/models/History'
import { useGlobalStore } from '@/stores/globalStore'
import { useWaterDetailStore } from '@/stores/waterDetailStore'
import { useInitDataStore } from '@/stores/initDataStore'
import { ALERT_COLORS, ALERT_COLORS2 } from '@/lib/constants'

type Point = {
  x: number
  y: number
  d: HistoryItem
}

type Alert = {
  y: number
  color: string
  title: string
  comparison: number
}

type Props = {
  resolution: string
  date: Date
}


export default /*#__PURE__*/_defineComponent({
  props: {
    resolution: null,
    date: null
  },
  setup(__props: any) {

const props = __props as {
  resolution: string
  date: Date
}



const MAX_HEIGHT = 265
const Y_UNITS_NUM = 8
const POINTS_NUM = 13
const UNITS = {
  half_day: 60 * 60,
  ten_min: 60 * 10,
} as { [key: string]: number }

const showTooltip = ref<boolean>(false)
const tooltipLeft = ref<number>(0)
const tooltipTop = ref<number>(0)
const tooltipTitle = ref<string>('')
const tooltipValue = ref<number>(0)

const globalStore = useGlobalStore()
const waterDetailStore = useWaterDetailStore()
const initDataStore = useInitDataStore()

const ap = computed(() => {
  const station = globalStore.currentStation

  return station?.ap
})

const alertList = computed(() => {
  const thInfoColors = initDataStore.thresholdColorInfo

  return thInfoColors || (ap.value === undefined ? ALERT_COLORS : ALERT_COLORS2)
})

const alerts = computed(() => {
  return Object.keys(alertList.value)
    .map(alert => {
      const thresholds = globalStore.waterThreshold
      const t = thresholds.find(th => camelCase(th.status) === alert)

      if (!t) {
        return
      }

      return {
        y: MAX_HEIGHT - calcHeight(t.comparison),
        color: alertList.value[alert as keyof typeof ALERT_COLORS],
        title: t.title,
        comparison: t.comparison,
      } as Alert
    })
    .filter(a => a !== undefined) as Alert[]
})

// グラフ表示の日付の範囲
const timeRange = computed(() => {
  const end = moment(props.date)
  const start = moment(end).add(-UNITS[props.resolution] * POINTS_NUM, 'seconds')

  return { start, end }
})

const data = computed(() => {
  return reverse([...waterDetailStore.history])
    .filter(item => {
      const time = moment(item.time)
      const { start, end } = timeRange.value

      return time.isBetween(start, end, undefined, '[]')
    })
    .slice(-POINTS_NUM)
})

const calcHeight = (value: number) => {
  const scale = globalStore.currentStation?.scale || { min: 0, max: 7, span: 7 }

  return (MAX_HEIGHT / (scale.max - scale.min)) * (value - scale.min)
}

const points = computed(() => {
  const { start } = timeRange.value
  const points = [] as Point[]

  if (data.value.length === 0) {
    return []
  }

  times(POINTS_NUM, index => {
    const time = moment(start)
      .add((index + 1) * UNITS[props.resolution], 'seconds')
      .format('HH:mm')

    const datum = data.value.find(d => moment(d.time).format('HH:mm') === time)

    if (datum === undefined || datum.time === undefined) {
      return
    }

    const value = datum.waterLevel

    if (value === undefined) {
      return
    }

    const x = 43 * index
    const y = MAX_HEIGHT - calcHeight(value)

    points.push({ x, y, d: datum })
  })

  return points.filter(p => p)
})

const pointStyle = (point: Point) => {
  if (point.y > MAX_HEIGHT) {
    return {}
  }

  return {
    left: `${point.x}px`,
    top: `${point.y}px`,
  }
}

const yUnits = computed(() => {
  const scale = globalStore.currentStation?.scale || { min: 0, max: 7, span: 7 }
  const diff = scale.max - scale.min
  const span = diff / (Y_UNITS_NUM - 1)
  const units = [] as number[]
  const apValue = ap.value || 0

  times(Y_UNITS_NUM, index => {
    units.unshift(scale.min + index * span + apValue)
  })

  return units
})

const xUnits = computed(() => {
  const { start } = timeRange.value
  const units = [] as string[]
  const UNIT_NUM = Math.floor(POINTS_NUM / 2)

  times(UNIT_NUM, index => {
    const time = moment(start)
      .add((index + 1) * UNITS[props.resolution] * 2, 'seconds')
      .format('HH:mm')

    units.push(time)
  })

  return units
})

const handleMouseenter = (datum: HistoryItem, e: MouseEvent) => {
  const $plotArea = $('.plot-area')
  const offset = $plotArea.offset()
  const x = e.pageX - (offset?.left || 0)
  const y = e.pageY - (offset?.top || 0)
  const apValue = ap.value || 0

  showTooltip.value = true
  tooltipTop.value = y
  tooltipLeft.value = x - 80
  tooltipTitle.value = moment(datum.time).format('HH:mm')
  tooltipValue.value = datum.waterLevel + apValue
}
const handleMouseleave = () => {
  showTooltip.value = false
}
const handleMouseenterTh = (alert: Alert, e: MouseEvent) => {
  const offsetX = $('.plot-area').offset()?.left || 0
  const x = e.pageX - offsetX
  const apValue = ap.value || 0

  showTooltip.value = true
  tooltipTop.value = alert.y + 5
  tooltipLeft.value = x + 10
  tooltipTitle.value = alert.title
  tooltipValue.value = alert.comparison + apValue
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _hoisted_4,
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(xUnits), (unit) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: unit,
                    class: "x-unit"
                  }, _toDisplayString(unit), 1))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(yUnits), (unit) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: unit,
                    class: "y-unit"
                  }, _toDisplayString(unit.toFixed(1)) + "m", 1))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_10, [
                _withDirectives(_createVNode(Tooltip, {
                  top: tooltipTop.value,
                  left: tooltipLeft.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(tooltipTitle.value), 1),
                    _hoisted_11,
                    _createTextVNode(" " + _toDisplayString(tooltipValue.value.toFixed(2)) + "m ", 1)
                  ]),
                  _: 1
                }, 8, ["top", "left"]), [
                  [_vShow, showTooltip.value]
                ]),
                (_openBlock(), _createElementBlock("svg", _hoisted_12, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Math.max(0, _unref(points).length - 1), (index) => {
                    return (_openBlock(), _createElementBlock("line", {
                      key: index,
                      x1: _unref(points)[index - 1]?.x,
                      y1: _unref(points)[index - 1]?.y,
                      x2: _unref(points)[index]?.x,
                      y2: _unref(points)[index]?.y,
                      stroke: "#0076C8",
                      "stroke-width": "2"
                    }, null, 8, _hoisted_13))
                  }), 128)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(alerts), (alert, index) => {
                    return (_openBlock(), _createElementBlock("line", {
                      key: index,
                      x1: 0,
                      y1: alert?.y,
                      x2: 520,
                      y2: alert?.y,
                      stroke: alert?.color,
                      "stroke-width": "2",
                      onMouseenter: ($event: any) => (handleMouseenterTh(alert, $event)),
                      onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (showTooltip.value = false))
                    }, null, 40, _hoisted_14))
                  }), 128))
                ])),
                _createElementVNode("div", _hoisted_15, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(points), (point) => {
                    return (_openBlock(), _createElementBlock("div", {
                      onMouseenter: ($event: any) => (handleMouseenter(point.d, $event)),
                      onMouseleave: handleMouseleave,
                      key: point?.x,
                      style: _normalizeStyle(pointStyle(point)),
                      class: "chobo"
                    }, _hoisted_18, 44, _hoisted_16))
                  }), 128))
                ])
              ])
            ])
          ])
        ])
      ])
    ])
  ]))
}
}

})